import { db } from '../firebaseConfig';
import { collection, addDoc, query, where, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import JSZip from "jszip";
import Papa from 'papaparse';

const currentDate = sessionStorage.getItem('date') || new Date().toISOString().split('T')[0];
console.log("Aktuelles Datum aus sessionStorage:", currentDate);

const DBService = {
    uploadCsv: async (file, collectionName) => {
        return new Promise((resolve, reject) => {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: async (result) => {
                    const data = result.data;
                    try {
                        const BATCH_SIZE = 500; // Batching für große Datenmengen
                        for (let i = 0; i < data.length; i += BATCH_SIZE) {
                            const batchData = data.slice(i, i + BATCH_SIZE);
                            const batch = batchData.map(async (row) => {
                                await addDoc(collection(db, collectionName), row);
                            });
                            await Promise.all(batch);
                        }
                        resolve("CSV successfully uploaded!");
                    } catch (error) {
                        console.error("Error saving data:", error);
                        reject(error);
                    }
                },
                error: (error) => reject(error),
            });
        });
    },

    fetchData: async (collectionName, filters = {}) => {
        try {
            let colRef = collection(db, collectionName);
            if (filters.field && filters.value) {
                colRef = query(colRef, where(filters.field, '==', filters.value));
            }
            const querySnapshot = await getDocs(colRef);
            return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("Error fetching data:", error);
            throw error;
        }
    },

    fetchDataByDate: async (collectionName, date) => {
        try {
            if (!date || date === "null") {
                throw new Error('Ein gültiges Datum muss angegeben werden.');
            }

            // Firebase Query mit Filter nach Datum
            const colRef = collection(db, collectionName);
            const dateQuery = query(colRef, where('datum', '==', date));
            const querySnapshot = await getDocs(dateQuery);

            return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error(`Fehler beim Abrufen der Daten aus der Collection "${collectionName}" für Datum "${date}":`, error);
            throw error;
        }
    },

    updateData: async (collectionName, pin, data) => {
        try {
            // Dokument anhand der Pin abfragen
            const colRef = collection(db, collectionName);
            const pinQuery = query(colRef, where('pin', '==', pin));
            const querySnapshot = await getDocs(pinQuery);

            if (querySnapshot.empty) {
                throw new Error(`Kein Dokument mit der Pin "${pin}" gefunden.`);
            }

            // Update für das erste gefundene Dokument
            const docRef = querySnapshot.docs[0].ref;
            await updateDoc(docRef, data);

            console.log(`Daten erfolgreich aktualisiert für Pin "${pin}".`);
            return true;
        } catch (error) {
            console.error(`Fehler beim Aktualisieren der Daten für Pin "${pin}":`, error);
            throw error;
        }
    },


    deleteData: async (collectionName, id) => {
        try {
            const docRef = doc(db, collectionName, id);
            await deleteDoc(docRef);
            return "Data successfully deleted!";
        } catch (error) {
            console.error("Error deleting data:", error);
            throw error;
        }
    },

    validateLogin: async (datum, zugangscode, status) => {
        try {
            // Direktes Datum verwenden
            const dateQuery = query(
                collection(db, 'LOGIN'),
                where('datum', '==', datum) // Datum direkt übergeben
            );
            const dateSnapshot = await getDocs(dateQuery);
            if (dateSnapshot.empty) return false;

            // Filter nach Status
            const statusFiltered = dateSnapshot.docs.filter((doc) =>
                doc.data().status.toLowerCase() === status.toLowerCase()
            );
            if (statusFiltered.length === 0) return false;

            // Zugangscode prüfen
            const accessCodeMatch = statusFiltered.find(
                (doc) => doc.data().zugangscode === zugangscode
            );
            return !!accessCodeMatch;
        } catch (error) {
            console.error("Error validating login:", error);
            throw error;
        }
    },

    getDistinctDates: async () => {
        try {
            const colRef = collection(db, 'LOGIN');
            const querySnapshot = await getDocs(colRef);

            // Extract distinct dates from the 'datum' field
            const dates = new Set();
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                if (data.datum) {
                    dates.add(data.datum);
                }
            });

            return Array.from(dates).sort(); // Return sorted array of distinct dates
        } catch (error) {
            console.error('Error fetching distinct dates:', error);
            throw error;
        }
    },

    clearCollection: async (collectionName) => {
        try {
            const colRef = collection(db, collectionName);
            const querySnapshot = await getDocs(colRef);

            if (querySnapshot.empty) {
                console.log(`Collection "${collectionName}" ist bereits leer.`);
                return "Collection is already empty.";
            }

            // Lösche alle Dokumente in der Collection
            const deletePromises = querySnapshot.docs.map((doc) => deleteDoc(doc.ref));
            await Promise.all(deletePromises);

            console.log(`Collection "${collectionName}" erfolgreich geleert.`);
            return "Collection successfully cleared.";
        } catch (error) {
            console.error(`Fehler beim Leeren der Collection "${collectionName}":`, error);
            throw error;
        }
    },

    downloadAllCollections: async (categories) => {
        try {
            const zip = new JSZip();
    
            for (const category of categories) {
                const collectionName = category.value;
                const querySnapshot = await getDocs(collection(db, collectionName));
                let jsonData = [];
    
                querySnapshot.forEach(doc => {
                    let data = doc.data();
                    delete data.id; // Entfernt die 'id', falls sie als Feld existiert
                    jsonData.push(data);
                });
    
                if (jsonData.length > 0) {
                    const csvData = Papa.unparse(jsonData, {
                        columns: Object.keys(jsonData[0]) // Explizite Spalten ohne 'id'
                    });
                    zip.file(`${collectionName}.csv`, csvData);
                }
            }
    
            const content = await zip.generateAsync({ type: "blob" });
            return content; // Gibt das ZIP als Blob zurück
        } catch (error) {
            console.error("Fehler beim Herunterladen der Collections als CSV:", error);
            throw error;
        }
    }
};

export default DBService;
